import dataCollector from 'braintree-web/data-collector';
import googlePayment from 'braintree-web/google-payment';
import threeDSecure from 'braintree-web/three-d-secure';
import { upperCase } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import cookies from 'react-cookies';
import { useSelector } from 'react-redux';
import { PAYMENT_METHODS, PAYMENT_TYPES } from '../config/constants/payment';
import COOKIES from '../config/cookies/cookies';
import {
  DEFAULT_PAYMENT_ERROR,
  ERROR_MESSAGE_BRAINTREE,
  LIABILITY_ERROR,
} from '../config/text/text';
import { trackEvent } from '../helpers/analytics';
import { getExpressBuyToken } from '../helpers/api';
import BraintreeService from '../helpers/braintreeService';
import { setCookie } from '../helpers/cookieSetter';
import CustomErrorsService from '../helpers/customErrorsService';
import errorTrackingService from '../helpers/errorTrackingService';
import { isMobileViewport } from '../helpers/screen';
import { getUserStatus } from '../helpers/userStatus';
import ThemeContext from '../providers/ThemeProvider';
import { registerUser } from '../redux/actions/user';
import { logToSplunk } from './_generic/axiosSplunk/axiosSplunk';
import Spinner from './_generic/spinner/Spinner';
import useToast from './_generic/toast/UseToast';

const GooglePayExpressCheckout = ({
  basketProducts,
  checkoutData,
  finalOrderAmount,
  orderSummary,
  orderComplete,
  totalQuantity,
  isPostCheckout,
  customError,
  afterError,
  oneClickCheckout,
  noMargin = false,
}) => {
  const toast = useToast();
  const [threeDSecureInstance, setThreeDSecureInstance] = useState(null);
  const [instance, setInstance] = useState(null);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const isWalletApplied = false;
  const theme = useContext(ThemeContext);
  const countryCode = process.env.NEXT_PUBLIC_COUNTRY_CODE;
  const locationCookie = cookies?.load(COOKIES?.location);
  const [locations] = useSelector((state) => [state.locations.locations]);
  const isMobile = isMobileViewport();
  const locationList = useMemo(() => {
    return locations
      .filter((loc) => loc.countryCode === countryCode)
      .map((loc) => ({
        ...loc,
        lowerName: loc.name?.toLowerCase(),
        lowerShortName: loc.shortName?.toLowerCase(),
      }));
  }, [locations, countryCode]);

  const getValidLocation = (locality) => {
    const lowerCaseLocality = locality.toLowerCase();
    const res = locationList.find(
      (item) =>
        item.lowerShortName === lowerCaseLocality ||
        item.lowerName === lowerCaseLocality,
    );

    return res?.shortName;
  };

  useEffect(() => {
    if (basketProducts?.length > 0 && instance) {
      const script = document.createElement('script');
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      script.async = true;
      script.onload = loadGooglePay;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [instance, finalOrderAmount]);

  useEffect(() => {
    if (isPostCheckout && instance) {
      const script = document.createElement('script');
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      script.async = true;
      script.onload = loadGooglePay;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isPostCheckout, instance]);

  const toggleLoaderOn = () => {
    setIsLoading(true);
  };

  const toggleLoaderOff = () => {
    setIsLoading(false);
  };

  const loadGooglePay = async () => {
    const paymentsClient = new window.google.payments.api.PaymentsClient({
      environment:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod' ? 'PRODUCTION' : 'TEST',
    });

    const paymentDataRequest = instance.createPaymentDataRequest({
      transactionInfo: {
        currencyCode: upperCase(orderSummary?.currency),
        totalPrice: finalOrderAmount?.toFixed(2).toString(),
        totalPriceStatus: 'FINAL',
      },
    });

    const isReadyToPayRequest = {
      allowedPaymentMethods: paymentDataRequest.allowedPaymentMethods,
      apiVersion: 2,
      apiVersionMinor: 0,
    };

    const isReadyToPay = await paymentsClient.isReadyToPay(isReadyToPayRequest);

    if (isReadyToPay.result) {
      // Clear any existing Google Pay button to prevent duplication
      const buttonContainer = document.querySelector('#google-pay-button');
      if (buttonContainer) {
        buttonContainer.innerHTML = ''; // Clear existing buttons
      }
      const buttonSizeMode = isMobile ? 'fill' : 'fit';
      const button = paymentsClient.createButton({
        buttonSizeMode,
        onClick: () => onGooglePaymentButtonClicked(paymentsClient),
      });
      document?.querySelector('#google-pay-button')?.appendChild(button);
    }
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const providerError = (error) => {
    console.log('error in providerError', error);
  };

  const onGooglePaymentButtonClicked = async (paymentsClient) => {
    toggleLoaderOn();
    trackEvent('express_checkout');
    const paymentDataRequest = instance.createPaymentDataRequest({
      allowedPaymentMethods: [
        {
          parameters: {
            billingAddressParameters: {
              format: 'FULL',
            },
            billingAddressRequired: true,
          },
          type: 'CARD',
        },
      ],
      emailRequired: true,
      transactionInfo: {
        currencyCode: upperCase(orderSummary.currency),
        totalPrice: finalOrderAmount?.toFixed(2).toString(),
        totalPriceStatus: 'FINAL',
      },
    });

    try {
      const paymentData = await paymentsClient.loadPaymentData(
        paymentDataRequest,
      );
      processPayment(paymentData);
    } catch (error) {
      console.error('Payment failed', error);
      notifyError(DEFAULT_PAYMENT_ERROR);
      logToSplunk(
        {
          component: 'googleExpressCheckout',
          error,
          type: 'google express checkout initial call',
        },
        'frontend logs',
      );
      toggleLoaderOff();
    }
  };

  const processPayment = async (paymentData) => {
    // To be removed
    logToSplunk(
      {
        component: 'googleExpressCheckout',
        paymentData,
        type: 'process payment',
      },
      'frontend logs',
    );
    // Send paymentData to your server for processing
    const name = paymentData?.paymentMethodData?.info?.billingAddress?.name;
    const [firstName, ...lastNameParts] = name.split(' ');
    const lastName = lastNameParts.join(' ');
    const retrievedEmail = paymentData?.email;
    const userEmail = cookies.load(COOKIES.temporaryEmail);
    let emailUsed;
    if (isPostCheckout) {
      emailUsed = cookies.load(COOKIES.expressCheckoutEmail);
    } else {
      emailUsed = userEmail ? userEmail : retrievedEmail;
      setCookie({
        expireInHours: 1,
        key: COOKIES.expressCheckoutEmail,
        value: emailUsed,
      });
    }

    // Step 1: Get user status using email address from payment shipping contact
    const userStatusResponse = await getUserStatus(emailUsed);

    if (userStatusResponse) {
      let authResponse; // Variable to store response from register or login API

      if (!isPostCheckout) {
        if (userStatusResponse.isNewUser) {
          // Step 2a: If new user, call express register API
          const subscriptionCookie = cookies.load(COOKIES.userSource);

          const values = {
            addressLine1:
              paymentData?.paymentMethodData?.info?.billingAddress?.address1,
            addressLine2:
              paymentData?.paymentMethodData?.info?.billingAddress?.address2,
            city:
              paymentData?.paymentMethodData?.info?.billingAddress?.locality ||
              locationCookie?.name,
            email: emailUsed,
            emailConfirmation: emailUsed,
            emailOptOut: cookies.load(COOKIES.emailOptOut),
            firstName,
            location:
              getValidLocation(
                paymentData?.paymentMethodData?.info?.billingAddress?.locality,
              ) || locationCookie?.name,
            postCode:
              paymentData?.paymentMethodData?.info?.billingAddress?.postalCode,
            rejectDMGTContact: false,
            subscriptionSource: subscriptionCookie
              ? decodeURIComponent(subscriptionCookie)
              : 'expressGooglePay',
            surname: lastName || '.',
            title: 'test',
          };
          // don't send autocomplete field to BE
          // eslint-disable-next-line fp/no-delete
          delete values.autocomplete;
          const isPasswordless = true;
          const data = await registerUser(values, isPasswordless);
          authResponse = data?.data;
          setCookie({
            expireInHours: 1,
            key: COOKIES.expressRegister,
            value: 'true',
          });
        } else {
          // Step 2b: If existing user, call login API (get express buy token)
          const data = await getExpressBuyToken(emailUsed);
          authResponse = data?.accessToken;
          localStorage.setItem(COOKIES.expressAuthToken, authResponse, 600_000);
        }
      } else {
        authResponse = localStorage.getItem(COOKIES.expressAuthToken);
      }

      if (authResponse) {
        // dispatch(loginSuccess(authResponse.data));

        // dispatch(setUserEmail(authResponse.userEmail)); // Dispatch action to set user email
        // Step 3: Call /token API to generate the token

        let clientInstance;
        let BTToken;
        if (userStatusResponse.isNewUser) {
          clientInstance = await BraintreeService.getClientInstance();
          BTToken = clientInstance?.data?.clientToken;

          // AuthenticatedToken = await BraintreeService.getClientToken();
          // BTToken = AuthenticatedToken?.data?.clientToken;
        } else {
          BTToken = authResponse;
          clientInstance = await BraintreeService.getGoogleLoggedOutClientInstance(
            BTToken,
          );
        }

        // const clientInstance = await BraintreeService.getGoogleLoggedOutClientInstance(
        //   BTToken,
        // );
        const googlePaymentInstance = await googlePayment.create({
          client: clientInstance,
          googleMerchantId: '11773631737842599402',
          googlePayVersion: 2, // Optional in sandbox; if set in sandbox, this value must be a valid production Google Merchant ID
        });
        // BraintreeService.totalAmountToPay = finalOrderAmount;
        const tokenizedPayload = await googlePaymentInstance?.parseResponse(
          paymentData,
        );

        if (userStatusResponse.isNewUser) {
          submitPayment({
            amount: finalOrderAmount,
            tokenizedPayload,
          });
        } else {
          submitPayment({
            amount: finalOrderAmount,
            expressBuyAuthToken: BTToken,
            tokenizedPayload,
          });
        }
      }
    }
  };
  const submitPayment = async ({
    amount,
    expressBuyAuthToken,
    tokenizedPayload,
  }) => {
    const emailUsed = cookies.load(COOKIES.expressCheckoutEmail);

    if (tokenizedPayload.details.isNetworkTokenized === false) {
      try {
        const payload = await threeDSecureInstance?.verifyCard({
          amount: amount.toFixed(2).toString(),
          bin: tokenizedPayload.details.bin,
          challengeRequested: true,
          nonce: tokenizedPayload.nonce,
          onLookupComplete: (data, next) => {
            logToSplunk(
              {
                data,
                email: emailUsed,
                event: 'lookupComplete',
              },
              'frontend logs - express buy',
            );
            next();
          },
        });
        if (payload?.liabilityShiftPossible && !payload?.liabilityShifted) {
          // Liablity possible, but failed challenge
          logToSplunk(
            {
              email: emailUsed,
              event: 'Express OTP Challenge - failed',
              expirationDate: `${payload.details.expirationMonth}/${payload.details.expirationYear}`,
              payload,
              reason: payload.threeDSecureInfo.status,
            },
            'frontend logs - express buy',
          );
          handleOrderError(LIABILITY_ERROR);
        } else {
          // Either the liability shift is not possible, or the liability shifted
          if (payload.nonce) tokenizedPayload.nonce = payload.nonce;
          logToSplunk(
            {
              email: emailUsed,
              event: 'Express OTP Challenge - successful',
              payload,
            },
            'frontend logs - express buy',
          );
          sendPayment(tokenizedPayload, expressBuyAuthToken);
        }
      } catch (error) {
        console.log('error in 3d', error?.message);
        logToSplunk(
          {
            component: 'googleExpressCheckout',
            email: emailUsed,
            error,
            type: 'google express checkout 2',
          },
          'frontend logs',
        );
        notifyError(error?.message);
        toggleLoaderOff();
      }
    } else {
      logToSplunk(
        {
          checkoutData,
          component: 'PaymentGoogle',
          email: emailUsed,
          isWalletAppliedBeforePayment: isWalletApplied,
          orderSummary,
          payment: 'Google Payment',
          totalAmountTopay: amount,
          type: 'google express checkout data sent to payment providers',
        },
        'frontend logs',
      );
      sendPayment(tokenizedPayload, expressBuyAuthToken);
    }
  };

  const sendPayment = (tokenizedPayload, expressBuyAuthToken) => {
    const expressCheckout = true;
    if (expressBuyAuthToken) {
      BraintreeService.preparePayment({
        basketProducts,
        credit: false,
        dataCollectorInstance,
        expressBuyAuthToken,
        expressCheckout,
        isWalletApplied,
        orderSummary,
        paymentMethod: PAYMENT_METHODS.card,
        tokenizationPayload: tokenizedPayload,
      });
    } else {
      BraintreeService.preparePayment({
        basketProducts,
        credit: false,
        dataCollectorInstance,
        expressCheckout,
        isWalletApplied,
        orderSummary,
        paymentMethod: PAYMENT_METHODS.card,
        tokenizationPayload: tokenizedPayload,
      });
    }
  };

  const handleOrderComplete = (order) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    toggleLoaderOff();
    // call order complete in payment component
    orderComplete(order);
  };

  const handleOrderError = (error) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }

    if (
      error.data &&
      error.data.errors &&
      Object.keys(error.data.errors).length > 0
    ) {
      // custom errors
      const customErrors = CustomErrorsService.parseErrors(
        error.data.errors,
        error.config.data || {},
      );
      customError({ errors: customErrors });
    } else {
      // default error message
      notifyError(DEFAULT_PAYMENT_ERROR);
    }

    toggleLoaderOff();
    // setPaymentInProcess(false);

    // will reload the checkout
    afterError();
  };

  const setupMethods = async (clientInstance) => {
    if (!clientInstance) return;
    if (threeDSecureInstance) {
      threeDSecureInstance.teardown();
      setThreeDSecureInstance(null);
    }
    const emailUsed = cookies.load(COOKIES.expressCheckoutEmail);
    try {
      const response = await threeDSecure.create({
        client: clientInstance,
        version: 2,
      });
      if (response) {
        setThreeDSecureInstance(response);
      } else {
        errorTrackingService.logError(DEFAULT_PAYMENT_ERROR);
        setIsLoading(false);
      }
    } catch (error) {
      logToSplunk(
        {
          component: 'googleExpressCheckout',
          email: emailUsed,
          error,
          type: 'google express checkout 3',
        },
        'frontend logs',
      );
      notifyError(error);
      toggleLoaderOff();
    }
    try {
      const response = await dataCollector.create({
        client: clientInstance,
        kount: true,
        paypal: true,
      });
      if (response) {
        // At this point, you should access the dataCollectorInstance.deviceData value and provide it
        // to your server, e.g. by injecting it into your form as a hidden input.
        setDataCollectorInstance(response);
        BraintreeService.deviceData = response.deviceData;
      }
    } catch (error) {
      if (error) {
        console.error(error);
        logToSplunk(
          {
            component: 'googleExpressCheckout',
            email: emailUsed,
            error,
            type: 'google express checkout 4',
          },
          'frontend logs',
        );
        toggleLoaderOff();

        return;
      }
    }
    try {
      const googlePaymentInstance = await googlePayment.create({
        client: clientInstance,
        googleMerchantId: '11773631737842599402',
        googlePayVersion: 2, // Optional in sandbox; if set in sandbox, this value must be a valid production Google Merchant ID
      });
      if (googlePaymentInstance) {
        setInstance(googlePaymentInstance);
        // toggleLoaderOff();
      }
    } catch (error) {
      console.error(`Error creating Google Pay Instance: ${error}`);
      logToSplunk(
        {
          component: 'googleExpressCheckout',
          email: emailUsed,
          error,
          type: 'google express checkout 5',
        },
        'frontend logs',
      );
      toggleLoaderOff();
      notifyError(ERROR_MESSAGE_BRAINTREE);
    }
  };
  const setupBraintree = async () => {
    // payment component will be disabled if user is not logged in
    const emailUsed = cookies.load(COOKIES.expressCheckoutEmail);
    BraintreeService.paymentComplete = handleOrderComplete;
    BraintreeService.paymentError = handleOrderError;
    BraintreeService.paymentType = PAYMENT_TYPES.google;
    BraintreeService.totalQuantity = totalQuantity;
    BraintreeService.providerError = providerError;
    try {
      const isLoggedOutUser = true;
      const clientInstance = await BraintreeService.getClientInstance(
        isLoggedOutUser,
      );
      if (clientInstance) {
        setupMethods(clientInstance);
      }
    } catch (error) {
      if (error) {
        console.error(error);
        logToSplunk(
          {
            component: 'googleExpressCheckout',
            email: emailUsed,
            error,
            type: 'google express checkout 6',
          },
          'frontend logs',
        );
        toggleLoaderOff();
      }
    }
  };

  useEffect(() => {
    setupBraintree();
  }, []);

  return (
    <div className="div-wrapper">
      {isLoading && <Spinner isFullScreen />}
      <div className="google-pay" id="google-pay-button" />
      {!isMobile && (
        <span className="google-pay-bottom-text">
          {'Click above to Place Your Order'}
        </span>
      )}
      <style jsx>{`
        .google-pay {
          margin-left: 0%;
        }
        .google-pay-bottom-text {
          font-weight: bold;
          text-align: center;
          margin: 0 auto;
          font-size: 12px;
          display: block;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) {
          .google-pay {
            margin-left: ${isPostCheckout && !noMargin ? '35%' : '0%'};
          }
          .div-wrapper {
            padding-right: ${oneClickCheckout === 'b' ? '40px' : '10px'};
            margin-left: 30px;
            margin-top: ${!isMobile && oneClickCheckout === 'b'
              ? '-12px'
              : '0px'};
          }
        }
      `}</style>
    </div>
  );
};

export default GooglePayExpressCheckout;
